import React from "react";
import { Button as RawButton } from "@/components/ui/button";
import { Card as RawCard } from "@/components/ui/card";
import { CheckCircle2, Calendar, Moon, Users, MapPin } from "lucide-react";
import { format } from "date-fns";
import { motion } from "framer-motion";

/**
 * @param {{
 *   confirmation: {
 *     hotel: {
 *       image: string;
 *       name: string;
 *       area: string;
 *       room_type: string;
 *       price_per_night: number;
 *     };
 *     checkin: Date;
 *     checkout: Date;
 *     guests: number;
 *     nights: number;
 *     totalAmount: number;
 *   };
 *   onBackToSearch: () => void;
 *   onCompleteBooking: () => void;
 *   showNextStep?: boolean;
 * }} props
 */
export default function AccommodationConfirmationView({
  confirmation,
  onBackToSearch,
  onCompleteBooking,
  showNextStep,
}) {
  const Button = /** @type {React.ComponentType<any>} */ (RawButton);
  const Card = /** @type {React.ComponentType<any>} */ (RawCard);

  const { hotel, checkin, checkout, guests, nights, totalAmount } = confirmation;

  return (
    <div className="min-h-screen bg-white flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.5 }}
        className="max-w-2xl w-full"
      >
        <Card className="p-8 sm:p-12 text-center shadow-2xl bg-white">
          {/* Success Icon */}
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ duration: 0.5, delay: 0.2, type: "spring" }}
            className="w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6 bg-[#0B2421]"
          >
            <CheckCircle2 className="h-12 w-12 text-[#D4AF37]" />
          </motion.div>

          {/* Heading */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.3 }}
          >
            <h1 className="text-3xl sm:text-4xl font-bold text-[#0B2421] mb-3">
              Accommodation Confirmed!
            </h1>
            <p className="text-lg text-gray-600 mb-8">
              Your stay has been reserved
            </p>
          </motion.div>

          {/* Hotel Info */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.4 }}
            className="text-left space-y-4 mb-8"
          >
            <div className="flex items-start gap-3">
              <div className="w-20 h-20 rounded-lg overflow-hidden flex-shrink-0 bg-gray-100">
                <img
                  src={hotel.image}
                  alt={hotel.name}
                  className="w-full h-full object-cover"
                />
              </div>
              <div>
                <h3 className="font-bold text-gray-600 text-sm leading-tight mb-1">
                  {hotel.name}
                </h3>
                <div className="flex items-center gap-1 text-gray-600 text-sm mb-1">
                  <MapPin className="h-3 w-3 text-[#D4AF37]" />
                  <span>{hotel.area}</span>
                </div>
                <div className="text-xs text-gray-600">{hotel.room_type}</div>
              </div>
            </div>

            {/* Info Grid */}
            <div className="grid grid-cols-2 gap-3 pt-4 border-t border-gray-100">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-[#0B2421] rounded-lg flex items-center justify-center flex-shrink-0">
                  <Calendar className="h-5 w-5 text-[#D4AF37]" />
                </div>
                <div>
                  <div className="text-xs text-gray-600">Check-in</div>
                  <div className="font-semibold text-gray-600 text-sm">
                    {format(checkin, "dd MMM yyyy")}
                  </div>
                </div>
              </div>

              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-[#0B2421] rounded-lg flex items-center justify-center flex-shrink-0">
                  <Calendar className="h-5 w-5 text-[#D4AF37]" />
                </div>
                <div>
                  <div className="text-xs text-gray-600">Check-out</div>
                  <div className="font-semibold text-gray-600 text-sm">
                    {format(checkout, "dd MMM yyyy")}
                  </div>
                </div>
              </div>

              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-[#0B2421] rounded-lg flex items-center justify-center flex-shrink-0">
                  <Moon className="h-5 w-5 text-[#D4AF37]" />
                </div>
                <div>
                  <div className="text-xs text-gray-600">Nights</div>
                  <div className="font-semibold text-[#0B2421]">{nights}</div>
                </div>
              </div>

              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-[#0B2421] rounded-lg flex items-center justify-center flex-shrink-0">
                  <div className="text-xs font-bold text-[#D4AF37]">SGD</div>
                </div>
                <div>
                  <div className="text-xs text-gray-600">Subtotal</div>
                  <div className="font-semibold text-[#0B2421]">SGD {totalAmount.toFixed(2)}</div>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Actions */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.5 }}
            className="space-y-3"
          >
            <Button
              onClick={onCompleteBooking}
              className="w-full h-14 bg-[#0B2421] hover:bg-[#0B2421]/85 text-white font-semibold text-lg"
            >
              Complete Booking Now
            </Button>
            <Button
              variant="ghost"
              onClick={onBackToSearch}
              className="w-full h-10 text-gray-600 hover:bg-transparent hover:underline hover:text-[#0B2421]/70"
            >
              Change Accommodation
            </Button>
          </motion.div>

          {/* Info Notice */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5, delay: 0.6 }}
            className="mt-6 p-4 border border-amber-200 bg-amber-50 rounded-xl"
          >
            <p className="text-sm text-amber-700">
              💡 You're all set! Payment will be processed on the final step.
            </p>
          </motion.div>
        </Card>
      </motion.div>
    </div>
  );
}
